import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const UserModal = ({ isOpen, onClose, user, onSave, companyRoles = [] }) => {
  const { t } = useTranslation();
  
  // Initialize user data with improved company role handling
  const [userData, setUserData] = useState(() => {
    // Start with default values
    const initialData = {
      username: '',
      password: '',
      name: '',
      email: '',
      role: 'technician',
      companyRole: '',
      active: true
    };
    
    // If we have a user being edited
    if (user) {
      // For existing users, ensure companyRole is properly formatted as a string ID
      let companyRoleValue = '';
      
      if (user.companyRole) {
        // Handle both object format and string format
        companyRoleValue = typeof user.companyRole === 'object' 
          ? user.companyRole._id 
          : user.companyRole;
      }
      
      return {
        ...initialData,
        ...user,
        companyRole: companyRoleValue
      };
    }
    
    return initialData;
  });

  const [errors, setErrors] = useState({});

  // For debugging
  useEffect(() => {
    console.log("Current userData state:", userData);
    console.log("User company role value:", userData.companyRole);
    console.log("Available roles:", companyRoles.map(r => ({ id: r._id, name: r.name })));
  }, [userData, companyRoles]);

  const validateForm = () => {
    const newErrors = {};
    if (!userData.username) newErrors.username = t('common.validation.required');
    if (!user && !userData.password) newErrors.password = t('common.validation.required');
    if (!userData.name) newErrors.name = t('common.validation.required');
    if (!userData.email) newErrors.email = t('common.validation.required');
    if (!userData.email.includes('@')) newErrors.email = t('common.validation.invalidEmail');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Submitting user data:", userData);
      onSave(userData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {user ? t('userManagement.editUser') : t('userManagement.newUser')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.username')} *</label>
            <input
              type="text"
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                errors.username ? 'border-red-500' : ''
              }`}
              value={userData.username}
              onChange={(e) => setUserData({ ...userData, username: e.target.value })}
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-500">{errors.username}</p>
            )}
          </div>

          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('userManagement.password')} *</label>
              <input
                type="password"
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                  errors.password ? 'border-red-500' : ''
                }`}
                value={userData.password}
                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.name')} *</label>
            <input
              type="text"
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                errors.name ? 'border-red-500' : ''
              }`}
              value={userData.name}
              onChange={(e) => setUserData({ ...userData, name: e.target.value })}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.email')} *</label>
            <input
              type="email"
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                errors.email ? 'border-red-500' : ''
              }`}
              value={userData.email}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.role')} *</label>
            <select
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={userData.role}
              onChange={(e) => setUserData({ ...userData, role: e.target.value })}
            >
              <option value="admin">{t('userManagement.roles.admin')}</option>
              <option value="officeAdmin">{t('userManagement.roles.officeAdmin')}</option>
              <option value="warehouseAdmin">{t('userManagement.roles.warehouseAdmin')}</option>
              <option value="technician">{t('userManagement.roles.technician')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.companyRole')}</label>
            {companyRoles.length > 0 ? (
                <select
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={userData.companyRole || ''}
                onChange={(e) => setUserData({ ...userData, companyRole: e.target.value })}
                >
                <option value="">{t('userManagement.selectCompanyRole')}</option>
                {companyRoles.map(role => (
                    <option key={role._id} value={role._id}>{role.name}</option>
                ))}
                </select>
            ) : (
                <div className="mt-1 text-sm text-gray-500">
                {t('userManagement.noCompanyRoles', 'No company roles defined')}
                </div>
            )}
            </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="active-status"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={userData.active}
              onChange={(e) => setUserData({ ...userData, active: e.target.checked })}
            />
            <label htmlFor="active-status" className="ml-2 block text-sm text-gray-900">{t('userManagement.active')}</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {user ? t('common.actions.update') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;